import type { Plugin } from "../types";
import { setCache, getCache } from "../../__utils__/cache";
import type { CachedData } from "../../__utils__/cache";
import { setCachePromise, getCachePromise } from "../../__utils__/cachePromise";
import { trigger, subscribe } from "../../__utils__/cacheSubscribe";
import { onUnmounted, toValue } from "vue";

const useCachePlugin: Plugin<any, any[]> = (
  fetchInstance,
  {
    cacheKey,
    cacheTime = 5 * 60 * 1000,
    staleTime = 0,
    setCache: customSetCache,
    getCache: customGetCache
  }
) => {
  let unSubscribe: () => void;

  let currentPromise: Promise<any>;

  const _setCache = (key: string, cachedData: CachedData) => {
    if (customSetCache) {
      customSetCache(cachedData);
    } else {
      setCache(key, cacheTime, cachedData);
    }
    trigger(key, cachedData.data);
  };

  const _getCache = (key: string, params: any[] = []) => {
    if (customGetCache) {
      return customGetCache(params);
    }
    return getCache(key);
  };

  if (!cacheKey) {
    return {};
  }

  // get data from cache when init
  const cacheData = _getCache(cacheKey);
  if (cacheData && Object.hasOwnProperty.call(cacheData, "data")) {
    fetchInstance.data.value = toValue(cacheData.data);
    fetchInstance.params.value = toValue(cacheData.params);
    if (staleTime === -1 || new Date().getTime() - cacheData.time <= staleTime) {
      fetchInstance.loading.value = false;
    }
  }

  // subscribe same cachekey update, trigger update
  unSubscribe = subscribe(cacheKey, (data) => {
    fetchInstance.data.value = data;
  });

  onUnmounted(() => {
    unSubscribe?.();
  });

  return {
    onBefore: (params) => {
      const cacheData = _getCache(cacheKey, params);

      if (!cacheData || !Object.hasOwnProperty.call(cacheData, "data")) {
        return {};
      }

      // If the data is fresh, stop request
      if (staleTime === -1 || new Date().getTime() - cacheData.time <= staleTime) {
        return {
          loading: false,
          data: cacheData?.data,
          error: undefined,
          returnNow: true
        };
      } else {
        // If the data is stale, return data, and request continue
        return {
          data: cacheData?.data,
          error: undefined
        };
      }
    },
    onRequest: (service, args) => {
      let servicePromise = getCachePromise(cacheKey);

      // If has servicePromise, and is not trigger by self, then use it
      if (servicePromise && servicePromise !== currentPromise) {
        return { servicePromise };
      }

      servicePromise = service(...args);
      currentPromise = servicePromise;
      setCachePromise(cacheKey, servicePromise);
      return { servicePromise };
    },
    onSuccess: (data, params) => {
      if (cacheKey) {
        // cancel subscribe, avoid trgger self
        unSubscribe?.();
        _setCache(cacheKey, {
          data,
          params,
          time: new Date().getTime()
        });
        // resubscribe
        unSubscribe = subscribe(cacheKey, (d) => {
          fetchInstance.data.value = d;
        });
      }
    },
    onMutate: (data) => {
      if (cacheKey) {
        // cancel subscribe, avoid trigger self
        unSubscribe?.();
        _setCache(cacheKey, {
          data,
          params: fetchInstance.params,
          time: new Date().getTime()
        });
        // resubscribe
        unSubscribe = subscribe(cacheKey, (d) => {
          fetchInstance.data.value = d;
        });
      }
    }
  };
};

export default useCachePlugin;
